<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
  <li ref="menuItemElement" @keyup.tab="focused && (showToggle = true)">
    <div
      v-if="children.length"
      class="h-full"
      @mouseenter="showMenu"
      @mouseleave="hideMenu"
    >
      <div class="flex h-full gap-4">
        <RevLink
          :aria-controls="`submenu-${id}`"
          :aria-expanded="activeMenu === id"
          aria-haspopup="true"
          class="text-action-default-hi body-2 h-full text-left transition-all duration-700 ease-in-out hover:body-2-bold hover:no-underline"
          :class="{
            'body-2-bold': isActivePage(route, url) && !isMenuActive,
            'text-action-default-low': isMenuActive,
          }"
          role="menuitem"
          :to="url"
          :tracking="{
            zone: 'header',
            name: 'department',
            value: title,
          }"
          :underlined="false"
          @click.capture="
            (event: Event) => {
              if (!isHoverMedia && activeMenu !== id) event.preventDefault()
              else hideMenu()
            }
          "
        >
          <div
            class="relative flex h-full items-center text-nowrap after:bg-action-default-hi after:pointer-events-none after:absolute after:-bottom-[1px] after:left-0 after:h-[2px] after:w-full after:opacity-0 after:content-[''] hover:after:opacity-100"
            :class="{
              'after:opacity-100': isActivePage(route, url) && !isMenuActive,
            }"
          >
            <span class="bold-hack" :title> {{ title }}</span>
          </div>
        </RevLink>
        <RevLink
          v-show="showToggle"
          :aria-controls="`submenu-${id}`"
          :aria-expanded="activeMenu === id"
          aria-haspopup="true"
          :aria-label="
            activeMenu === id
              ? i18n(translations.hideSubmenu, { title: title })
              : i18n(translations.showSubmenu, { title: title })
          "
          class="flex h-full items-center"
          @click="activeMenu === id ? hideMenu() : showMenu()"
          ><IconChevronDown
        /></RevLink>
      </div>

      <div class="absolute inset-x-0 z-[1] mt-1">
        <Transition
          :css="withTransition"
          enter-active-class="ease-out duration-300"
          enter-from-class="[clip-path:inset(0_0_100%)]"
          enter-to-class="[clip-path:inset(0_0_0%)]"
          leave-active-class="ease-in duration-100"
          leave-from-class="[clip-path:inset(0_0_0%)]"
          leave-to-class="[clip-path:inset(0_0_100%)]"
          @after-enter="withTransition = false"
        >
          <div
            v-show="activeMenu === id"
            :id="`submenu-${id}`"
            class="bg-surface-default-low flex transition-[clip-path]"
            role="menu"
          >
            <NavServiceList
              v-for="service in details"
              :key="service.name"
              class="bg-surface-default-mid max-w-256"
              :service
            />
            <NavMenuContent
              class="flex-1"
              :items="children"
              :universeTitle="title"
              :universeURL="url"
              @click-item="hideMenu"
            />
          </div>
        </Transition>
      </div>
    </div>
    <RevLink
      v-else
      class="body-2 text-nowrap transition-all duration-700 ease-in-out hover:body-2-bold hover:no-underline"
      :class="{
        'body-2-bold': isActivePage(route, url) && !isMenuActive,
        'text-action-default-low': isMenuActive,
      }"
      :style="{ color: color }"
      :to="url"
      :tracking="{
        zone: 'header',
        name: 'department',
        value: title,
      }"
      :underlined="false"
    >
      <div
        class="relative flex h-full items-center text-nowrap after:bg-action-default-hi after:pointer-events-none after:absolute after:-bottom-[1px] after:left-0 after:h-[2px] after:w-full after:opacity-0 after:content-[''] hover:after:opacity-100"
        :class="[
          isActivePage(route, url) && !isMenuActive ? 'after:opacity-100' : '',
        ]"
      >
        <component :is="resolveNavIcon(icon)" v-if="icon" />
        <span class="bold-hack" :title>{{ title }}</span>
      </div>
    </RevLink>
  </li>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevLink } from '@ds/components/Link'
import { IconChevronDown } from '@ds/icons/IconChevronDown'
import { useFocusWithin, useMediaQuery } from '@vueuse/core'

import { useActiveMenu } from '../../composables/useActiveMenu'
import type { NavigationItem } from '../../types'
import { isActivePage } from '../../utils/isActivePage'
import { resolveNavIcon } from '../../utils/navigationIcons'
import NavServiceList from '../NavBurgerMenu/NavServiceList.vue'

import translations from './NavMenu.translations'
import NavMenuContent from './NavMenuContent.vue'

const props = defineProps<NavigationItem & { position: number }>()

const menuItemElement = ref()
const showToggle = ref(false)
const timeout = ref()

const i18n = useI18n()
const isHoverMedia = useMediaQuery('(hover: hover)')
const { setActiveMenu, clearActiveMenu, activeMenu, isMenuActive } =
  useActiveMenu()
const route = useRoute()
const { trackHoverOver } = useTracking()
const router = useRouter()

function showMenu() {
  clearTimeout(timeout.value)
  timeout.value = setTimeout(() => {
    setActiveMenu(props.id)
    trackHoverOver({
      zone: 'header',
      name: 'department',
      value: props.title,
      page_type:
        router.currentRoute.value.name?.toString() ??
        router.currentRoute.value.fullPath,
      position: props.position,
    })
  }, 150)
}

function hideMenu() {
  clearTimeout(timeout.value)
  timeout.value = setTimeout(() => {
    clearActiveMenu()
  }, 150)
}

const withTransition = ref(true)
watch(isMenuActive, () => {
  if (!isMenuActive.value) {
    withTransition.value = true
  }
})

const { focused } = useFocusWithin(menuItemElement)
watch(focused, (isFocused) => {
  if (!isFocused) {
    hideMenu()
    showToggle.value = false
  }
})

onBeforeUnmount(() => {
  clearTimeout(timeout.value)
})
</script>

<style scoped>
.bold-hack {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
}

.bold-hack::after {
  content: attr(title);
  font-weight: 600;
  font-size: 0.875rem /* 14px */;
  line-height: 1.25rem /* 20px */;
  height: 0;
  overflow: hidden;
  visibility: hidden;
}
</style>
